import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  status: 'idle', // 'idle'|'starting'|'loading'|'active'|'submitting'|'complete'|'failed'|'mic-gate'|'mic-denied'
  error: null,
  instructionStatus: 'idle', // 'idle'|'loading'|'succeeded'|'failed'
  instructionError: null,
  instructionCourseId: null,
  instructionElementId: null,
  instructionResolvedElementId: null,
  instructionElementName: '',
  instructionElementType: '',
  instructionDomains: '',
  instructionQuestionCount: 0,
  instructionTimeLimit: null,
  instructionContinueAttemptId: null,
  instructionHideStart: 0,
  instructionAttemptRemaining: 0,
  instructionReviewAttemptId: null,
  // Mirrors the server-side `speech_is_on` flag. Once a mic check passes the
  // server remembers it, so the pre-lesson brief must not re-gate every
  // speaking element (triage item 41). null = not yet known.
  speechIsOn: null,
  attemptId: null,
  courseId: null,
  elementId: null,
  elementName: '',
  elementType: '',
  domains: '',
  attemptName: '',
  questionId: null,
  questionNo: 0,
  questionCount: 0,
  attemptProgress: 0,
  timeRemaining: null,
  question: null,
  isFeedback: false,
  feedbackData: null,
  currentAnswer: '',
  pendingCompletion: null,
  completionData: null,
  completionModalDismissed: false,
  upNext: null,
  isReview: false,
  reviewAnswers: [],
  audioRecordings: {},
  isMultiQuestion: false,
  additionalAnswer: '',
  questions: [],
  mediaPlaying: false,
  replayCount: null,
  slowNetworkSeen: false,
  submitError: '',
  recordAttemptsRemaining: 3,
  pendingStartAfterMic: null,
  // Set once when status first transitions to 'active' (lesson becomes
  // interactable). Subsequent 'active' transitions leave it alone — see the
  // idempotent guards in lessonLoaded / lessonNextQuestion / etc. Drives
  // CompletionScreen's "Time spent" display. Cleared via lessonCleared.
  lessonStartedAt: null,
};
const slice = createSlice({
  name: 'lesson',
  initialState,
  reducers: {
    lessonInstructionsRequested(state, action) {
      const { courseId, elementId, elementType, attemptRemaining } = action.payload;
      state.instructionStatus = 'loading';
      state.instructionError = null;
      state.instructionCourseId = courseId;
      state.instructionElementId = elementId;
      state.instructionResolvedElementId = null;
      state.instructionElementName = '';
      state.instructionElementType = elementType ?? '';
      state.instructionDomains = '';
      state.instructionQuestionCount = 0;
      state.instructionTimeLimit = null;
      state.instructionContinueAttemptId = null;
      state.instructionHideStart = 0;
      state.instructionAttemptRemaining = attemptRemaining ?? 0;
      state.instructionReviewAttemptId = null;
    },
    lessonInstructionsLoaded(state, action) {
      state.instructionStatus = 'succeeded';
      state.instructionError = null;
      state.instructionCourseId = action.payload.courseId;
      state.instructionElementId = action.payload.elementId;
      state.instructionResolvedElementId = action.payload.resolvedElementId ?? null;
      state.instructionElementName = action.payload.elementName ?? '';
      state.instructionElementType = action.payload.elementType ?? '';
      state.instructionDomains = action.payload.domains ?? '';
      state.instructionQuestionCount = action.payload.questionCount ?? 0;
      state.instructionTimeLimit = action.payload.timeLimit ?? null;
      state.instructionContinueAttemptId = action.payload.continueAttemptId ?? null;
      state.instructionHideStart = action.payload.hideStart ?? 0;
      state.instructionAttemptRemaining = action.payload.attemptRemaining ?? 0;
      state.instructionReviewAttemptId = action.payload.reviewAttemptId ?? null;
    },
    lessonInstructionsFailed(state, action) {
      state.instructionStatus = 'failed';
      state.instructionError = action.payload ?? 'Failed to load lesson instructions';
    },
    lessonStartRequested(state, action) {
      state.status = 'starting';
      state.courseId = action.payload.courseId;
      state.elementId = action.payload.elementId;
      state.elementType = action.payload.elementType ?? state.elementType;
      state.error = null;
      state.pendingCompletion = null;
      state.completionData = null;
      state.completionModalDismissed = false;
      state.upNext = null;
      state.isReview = false;
      state.reviewAnswers = [];
      state.pendingStartAfterMic = null;
      state.lessonStartedAt = null;
    },
    lessonStarted(state, action) {
      state.attemptId = action.payload.attempt_id;
      state.attemptName = action.payload.attempt_name ?? '';
      state.status = 'loading';
    },
    lessonLoaded(state, action) {
      state.status = 'active';
      state.isReview = false;
      state.reviewAnswers = [];
      if (state.lessonStartedAt === null) state.lessonStartedAt = Date.now();
      state.questionId = action.payload.questionId;
      state.questionNo = action.payload.questionNo;
      state.questionCount = action.payload.questionCount;
      state.attemptProgress = action.payload.attemptProgress;
      state.timeRemaining = action.payload.timeRemaining;
      state.question = action.payload.question;
      state.elementName = action.payload.elementName;
      state.elementType = action.payload.elementType;
      state.domains = action.payload.domains ?? '';
      state.currentAnswer = '';
      state.isFeedback = false;
      state.feedbackData = null;
      state.audioRecordings = {};
      state.additionalAnswer = '';
      state.isMultiQuestion = action.payload.isMultiQuestion ?? false;
      state.questions = action.payload.questions ?? [];
      state.mediaPlaying = false;
      state.replayCount = action.payload.replayCount ?? null;
      state.slowNetworkSeen = false;
      state.recordAttemptsRemaining = 3;
    },
    lessonReviewLoaded(state, action) {
      state.status = 'active';
      state.isReview = true;
      state.reviewAnswers = action.payload.reviewAnswers ?? [];
      state.attemptId = action.payload.attemptId ?? null;
      state.attemptName = action.payload.attemptName ?? '';
      state.courseId = action.payload.courseId ?? state.courseId;
      state.elementId = action.payload.elementId ?? state.elementId;
      state.elementName = action.payload.elementName ?? '';
      state.elementType = action.payload.elementType ?? '';
      state.questionCount = action.payload.questionCount ?? state.reviewAnswers.length;
      state.questionNo = 0;
      state.questionId = null;
      state.question = null;
      state.isFeedback = false;
      state.feedbackData = null;
      state.currentAnswer = '';
      state.audioRecordings = {};
      state.additionalAnswer = '';
      state.mediaPlaying = false;
      state.timeRemaining = null;
      state.lessonStartedAt = null;
    },
    lessonAnswerChanged(state, action) {
      if (state.status === 'submitting' || state.isFeedback) return;
      state.submitError = '';
      state.currentAnswer = action.payload;
    },
    lessonAnswerSubmitting(state) {
      state.submitError = '';
      state.status = 'submitting';
    },
    lessonNextQuestion(state, action) {
      state.status = 'active';
      if (state.lessonStartedAt === null) state.lessonStartedAt = Date.now();
      state.questionId = action.payload.questionId;
      state.questionNo = action.payload.questionNo;
      state.questionCount = action.payload.questionCount;
      state.attemptProgress = action.payload.attemptProgress;
      state.timeRemaining = action.payload.timeRemaining ?? null;
      state.question = action.payload.question;
      state.currentAnswer = '';
      state.isFeedback = false;
      state.feedbackData = null;
      state.audioRecordings = {};
      state.additionalAnswer = '';
      state.mediaPlaying = false;
      state.replayCount = action.payload.replayCount ?? null;
      state.slowNetworkSeen = false;
      state.recordAttemptsRemaining = 3;
      state.isMultiQuestion = action.payload.isMultiQuestion ?? false;
      state.questions = action.payload.questions ?? [];
    },
    lessonFeedbackReady(state, action) {
      state.status = 'active';
      state.submitError = '';
      if (state.lessonStartedAt === null) state.lessonStartedAt = Date.now();
      state.isFeedback = true;
      state.feedbackData = action.payload.feedbackData;
      state.questionNo = action.payload.questionNo;
      state.questionCount = action.payload.questionCount;
      state.attemptProgress = action.payload.attemptProgress;
      state.timeRemaining = action.payload.timeRemaining ?? null;
      state.question = action.payload.nextQuestion ?? null;
      state.questionId = action.payload.nextQuestion?.question_id ?? state.questionId;
      state.recordAttemptsRemaining = 3;
      state.pendingCompletion = action.payload.pendingCompletion ?? null;
      state.currentAnswer = '';
      state.audioRecordings = {};
      state.additionalAnswer = '';
      state.mediaPlaying = false;
      state.replayCount = action.payload.replayCount ?? null;
      state.slowNetworkSeen = false;
      state.isMultiQuestion = action.payload.isMultiQuestion ?? false;
      state.questions = action.payload.questions ?? [];
    },
    lessonFeedbackDismissed(state) {
      state.isFeedback = false;
      state.feedbackData = null;
      if (state.question === null && state.pendingCompletion !== null) {
        state.status = 'complete';
        state.completionData = state.pendingCompletion;
        state.upNext = state.pendingCompletion?.completionModel?.upNext ?? state.upNext;
        state.pendingCompletion = null;
        state.completionModalDismissed = false;
        state.attemptProgress = 100;
        state.timeRemaining = null;
      }
    },
    lessonCompleted(state, action) {
      state.status = 'complete';
      state.completionData = action.payload;
      state.upNext = action.payload?.completionModel?.upNext ?? state.upNext;
      state.completionModalDismissed = false;
      state.attemptProgress = 100;
      state.timeRemaining = null;
    },
    lessonFailed(state, action) {
      state.status = 'failed';
      state.error = action.payload ?? 'Something went wrong';
      state.timeRemaining = null;
    },
    lessonCleared() {
      return initialState;
    },
    lessonTimerTick(state) {
      if (state.timeRemaining === null || state.timeRemaining <= 0) return;
      state.timeRemaining -= 1;
    },
    mediaPlaybackStarted(state) {
      state.mediaPlaying = true;
    },
    mediaPlaybackEnded(state) {
      state.mediaPlaying = false;
    },
    replayDecremented(state) {
      if (state.replayCount === null) return;
      state.replayCount = Math.max(state.replayCount - 1, 0);
    },
    slowNetworkAdvisoryShown(state) {
      state.slowNetworkSeen = true;
    },
    lessonRecordingStopped(state, action) {
      state.submitError = '';
      const {
        id,
        duration,
        mime,
        takeIndex,
        platform,
        filePath,
        fileName,
        valid,
        activity,
      } = action.payload;
      state.audioRecordings[id] = {
        duration,
        mime,
        takeIndex,
        ...(platform ? { platform } : {}),
        ...(filePath ? { filePath } : {}),
        ...(fileName ? { fileName } : {}),
        ...(typeof valid === 'boolean' ? { valid } : {}),
        ...(activity ? { activity } : {}),
      };
      state.recordAttemptsRemaining = Math.max(0, (state.recordAttemptsRemaining ?? 3) - 1);
    },
    lessonRecordingDiscarded(state, action) {
      state.submitError = '';
      delete state.audioRecordings[action.payload.id];
    },
    lessonAdditionalAnswerChanged(state, action) {
      state.submitError = '';
      state.additionalAnswer = action.payload;
    },
    lessonAnswerRejected(state, action) {
      // Recoverable submit rejection — user stays on the current question.
      // status: 'submitting' → 'active' so the question UI re-renders interactive.
      // submitError holds the server message for the UI to surface.
      // question / currentAnswer / audioRecordings / additionalAnswer all
      // untouched — the user keeps their recording and picks intact.
      state.status = 'active';
      if (state.lessonStartedAt === null) state.lessonStartedAt = Date.now();
      state.submitError = action.payload.message;
    },
    lessonMicGateRequired(state) {
      state.status = 'mic-gate';
    },
    lessonPreStartMicGateRequired(state, action) {
      state.status = 'mic-gate';
      state.pendingStartAfterMic = action.payload;
    },
    speechPrefsLoaded(state, action) {
      state.speechIsOn = action.payload === null ? null : !!action.payload;
    },
    preStartMicVerified(state) {
      state.speechIsOn = true;
    },
    micCheckPassed(state) {
      state.speechIsOn = true;
      state.status = state.pendingStartAfterMic ? 'starting' : 'active';
      if (!state.pendingStartAfterMic && state.lessonStartedAt === null) state.lessonStartedAt = Date.now();
    },
    lessonMicDenied(state) {
      state.status = 'mic-denied';
    },
    upNextReceived(state, action) {
      state.upNext = action.payload ?? null;
    },
    completionModalDismissed(state) {
      state.completionModalDismissed = true;
    },
    lessonCompletionContinueRequested() { /* saga trigger */ },
    reportQuestionRequested() { /* saga trigger; no state change */ },
    reportQuestionSucceeded() { /* saga symmetry */ },
    reportQuestionFailed() { /* toast handles UX */ },
    lessonVideoCheckRequested() { /* saga trigger; no state change */ },
  },
});

export const {
  lessonInstructionsRequested,
  lessonInstructionsLoaded,
  lessonInstructionsFailed,
  lessonStartRequested,
  lessonStarted,
  lessonLoaded,
  lessonReviewLoaded,
  lessonAnswerChanged,
  lessonAnswerSubmitting,
  lessonNextQuestion,
  lessonFeedbackReady,
  lessonFeedbackDismissed,
  lessonCompleted,
  lessonFailed,
  lessonCleared,
  lessonTimerTick,
  lessonRecordingStopped,
  lessonRecordingDiscarded,
  lessonAdditionalAnswerChanged,
  lessonAnswerRejected,
  mediaPlaybackStarted,
  mediaPlaybackEnded,
  replayDecremented,
  slowNetworkAdvisoryShown,
  lessonMicGateRequired,
  lessonPreStartMicGateRequired,
  speechPrefsLoaded,
  preStartMicVerified,
  micCheckPassed,
  lessonMicDenied,
  upNextReceived,
  completionModalDismissed,
  lessonCompletionContinueRequested,
  reportQuestionRequested,
  reportQuestionSucceeded,
  reportQuestionFailed,
  lessonVideoCheckRequested,
} = slice.actions;
export const lessonReducer = slice.reducer;
